import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { t } from '@reos/i18n';
import type { ChecklistTemplateDto, IntakeContextDto } from '@reos/field-intake';
import { caseRemaining, type RemainingGroup } from '../lib/case-remaining.js';
import type { LvAvailability } from '../lib/lv-api.js';
import { NAHLIZENI_DO_KN_PARCEL_URL } from '../lib/parcel-panel-rows.js';
import type { PrefillStatus } from '../lib/case-last-event.js';

/**
 * Krok ZAK1 body 2 a 5 — „CO ZBYVA ZJISTIT" na detailu zakazky.
 *
 * Nahore stav predvyplneni z otevrenych dat („Načítám údaje o nemovitosti…",
 * nebo datum nacteni s tlacitkem „Načíst znovu") a souhrn „Vyplněno samo: X
 * polí · zbývá Y", pod nim skupiny podle ZDROJE udaje s pocitadlem a proklikem
 * do naboru. Rozpad pocita cisty `lib/case-remaining.ts` nad sablonou ze
 * serveru, stav cte `prefillStatus` z casove osy — komponenta jen kresli.
 *
 * Mobil 390 px: skupiny jsou jeden radek pod druhym (`rh-a2-facts`, tataz
 * mrizka jako karta „Údaje nemovitosti"), takze se nic nezlomi.
 */

const cs = { locale: 'cs' } as const;

function groupCaption(group: RemainingGroup): string {
  return t(`webAdmin.caseDetail.remaining.source.${group.source}` as never, cs);
}

/**
 * Zustatek kreditu z knihy prichazi jako `numeric(14,4)` v textu
 * (`'1850.0000'`) — tak, jak ho ucetni kniha drzi. Makler ma videt castku, ne
 * tvar sloupce, takze se halere zahazuji az TADY, pri vykresleni; zaokrouhlit
 * uz v API by znamenalo poslat na obrazovku jine cislo, nez se uctuje.
 */
/** Den platnosti vypisu (`2026-10-29`) na to, co makler cte („29. 10. 2026"). */
function formatDay(isoDay: string): string {
  const parsed = new Date(`${isoDay}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? isoDay : parsed.toLocaleDateString('cs-CZ');
}

function formatCredit(balanceCzk: string | null): string {
  const value = Number(balanceCzk ?? '0');
  if (!Number.isFinite(value)) return balanceCzk ?? '0';
  return value.toLocaleString('cs-CZ', { maximumFractionDigits: 0 });
}

export function CaseRemainingCard({
  caseId,
  template,
  context,
  prefill = null,
  onReload,
  lv = null,
  onBuyLv,
  lvNote = null,
}: {
  readonly caseId: string;
  readonly template: ChecklistTemplateDto | null;
  readonly context: IntakeContextDto | null;
  /** Stav predvyplneni z casove osy; `null` = zakazka ho jeste nema. */
  readonly prefill?: PrefillStatus | null | undefined;
  /** „Načíst znovu" — `undefined` = tlacitko se nekresli (zakazka bez nemovitosti). */
  readonly onReload?: (() => void) | undefined;
  /**
   * Krok LV1: podklad pro „Stáhnout výpis z LV". `null` nebo
   * `available: false` = tlacitko se NEKRESLI VUBEC (priznak vypnuty, souhlas
   * ČÚZK chybi). O tom rozhoduje SERVER, obrazovka si priznak necte.
   */
  readonly lv?: LvAvailability | null | undefined;
  /** Potvrzeny nakup; `undefined` = probiha (tlacitko se nekresli). */
  readonly onBuyLv?: (() => void) | undefined;
  /** Vysledek nakupu nebo jeho chyba; `null` = nic se nehlasi. */
  readonly lvNote?: string | null | undefined;
}): React.JSX.Element | null {
  const [queued, setQueued] = React.useState(false);
  const lvOffered = lv !== null && lv !== undefined && lv.available && onBuyLv !== undefined;
  const remaining = caseRemaining(template, context);
  const loading = queued || prefill?.state === 'queued';
  if (remaining.groups.length === 0 && remaining.filled === 0 && !loading) return null;
  const summary =
    remaining.remaining === 0
      ? t('webAdmin.caseDetail.remaining.allDone', { ...cs, values: { filled: remaining.filled } })
      : remaining.filled === 0
        ? t('webAdmin.caseDetail.remaining.summaryNone', {
            ...cs,
            values: { remaining: remaining.remaining },
          })
        : t('webAdmin.caseDetail.remaining.summary', {
            ...cs,
            values: { filled: remaining.filled, remaining: remaining.remaining },
          });
  return (
    <section className="rh-a2-card" data-testid="case-remaining-card">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.caseDetail.remaining.heading', cs)}</h2>
      </div>
      {loading ? (
        <p className="rh-a2-card__sub" data-testid="case-prefill-loading">
          {t('webAdmin.caseDetail.remaining.loading', cs)}
        </p>
      ) : null}
      <p className="rh-a2-card__sub" data-testid="case-remaining-summary">
        {summary}
      </p>
      {lvNote === null || lvNote === undefined ? null : (
        <p className="rh-a2-card__sub" data-testid="case-remaining-lv-note">
          {lvNote}
        </p>
      )}
      {prefill !== null && prefill !== undefined && prefill.state === 'done' ? (
        <p className="rh-a2-card__foot" data-testid="case-prefill-loaded-at">
          {t('webAdmin.caseDetail.remaining.loadedAt', {
            ...cs,
            values: { date: new Date(prefill.at).toLocaleDateString('cs-CZ') },
          })}
        </p>
      ) : null}
      {onReload === undefined ? null : (
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--tinted"
          data-testid="case-prefill-reload"
          disabled={loading}
          onClick={() => {
            setQueued(true);
            onReload();
          }}
        >
          {queued
            ? t('webAdmin.caseDetail.remaining.reloadQueued', cs)
            : t('webAdmin.caseDetail.remaining.reload', cs)}
        </button>
      )}
      <ul className="rh-a2-facts">
        {remaining.groups.map((group) => (
          <li key={group.source} data-testid={`case-remaining-${group.source}`}>
            <span className="rh-a2-k">{groupCaption(group)}</span>
            <span className="rh-a2-v">
              {t('webAdmin.caseDetail.remaining.count', { ...cs, values: { count: group.count } })}{' '}
              {group.source === 'lv' ? (
                lvOffered ? (
                  /*
                    Krok LV1: priznak je zapnuty a ČÚZK souhlas dal (nebo jde
                    o interni testovaci kancelar), takze udaj z LV umime
                    DODAT — za kredit kancelare. Cena i zustatek jsou u
                    tlacitka, aby makler potvrzoval VYDAJ, ne „neco".
                  */
                  <>
                    <span className="rh-a2-note">
                      {' · '}
                      {/*
                        Uz zaplaceny vypis se NABIZI S DATEM (zadani bod 2):
                        katastr se meni po dnech, takze „uz to mame" nikdy
                        neznamena „je to aktualni". Kdyz je vypis z DNESKA,
                        rika se rovnou, ze dalsi stazeni nic nestoji - jinak
                        by makler nevedel, jestli druhym klikem plati znovu.
                      */}
                      {lv.lastPurchasedOn === null
                        ? t('webAdmin.caseDetail.remaining.lvBalance', {
                            ...cs,
                            values: { balance: formatCredit(lv.balanceCzk) },
                          })
                        : t('webAdmin.caseDetail.remaining.lvCachedAt', {
                            ...cs,
                            values: { date: formatDay(lv.lastPurchasedOn) },
                          })}
                      {lv.freeToday
                        ? ` · ${t('webAdmin.caseDetail.remaining.lvFreeToday', cs)}`
                        : ''}{' '}
                    </span>
                    <button
                      type="button"
                      className="rh-v4-btn rh-v4-btn--chip"
                      data-testid="case-remaining-lv-buy"
                      onClick={onBuyLv}
                    >
                      {lv.freeToday
                        ? t('webAdmin.caseDetail.remaining.lvRefresh', cs)
                        : lv.lastPurchasedOn === null
                          ? t('webAdmin.caseDetail.remaining.lvBuyPrice', {
                              ...cs,
                              values: { price: String(lv.priceCzk ?? 0) },
                            })
                          : /*
                              Starsi vypis -> „Stáhnout aktuální", ale S CENOU:
                              tohle uz je NOVA sestava za nove penize, ne
                              opakovane stazeni te zaplacene.
                            */
                            `${t('webAdmin.caseDetail.remaining.lvRefresh', cs)} (${String(lv.priceCzk ?? 0)} Kč)`}
                    </button>
                  </>
                ) : (
                  /*
                    Krok ZAK1 bod 4: pri vypnutem priznaku NESLIBUJEME „načteme
                    z katastru" — placene nacteni LV jeste neni. Makler vidi, ze
                    udaj doplni z vypisu sam, a odkaz do Nahlizeni do KN (KN4).
                  */
                  <>
                    <span className="rh-a2-note">
                      {' · '}
                      {t('webAdmin.caseDetail.remaining.lvHint', cs)}{' '}
                    </span>
                    <a
                      href={NAHLIZENI_DO_KN_PARCEL_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="rh-v4-btn rh-v4-btn--chip"
                      data-testid="case-remaining-lv-link"
                    >
                      {t('webAdmin.caseDetail.remaining.lvLink', cs)}
                    </a>
                  </>
                )
              ) : (
                <Link
                  to="/nabor"
                  search={{ caseId }}
                  className="rh-v4-btn rh-v4-btn--chip"
                  data-testid={`case-remaining-open-${group.source}`}
                >
                  {t('webAdmin.caseDetail.remaining.open', cs)}
                </Link>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
